import { useCallback, useEffect, useState } from "react";
import { KeyRound, Plus, UserX, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner } from "@/components/common/Primitives";
import { Field, Input, Select } from "@/components/common/Field";
import { FormDialog } from "@/components/vehicles/VehicleFormDialog";

const ROLES = ["admin", "fleet_manager", "garage_supervisor", "operator"];

export default function UsersPage() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const load = useCallback(() => api.get("/users").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const create = async () => { await api.post("/users", f); toast.success("User added"); load(); };
  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { is_active: !u.is_active }); toast.success(u.is_active ? "User deactivated" : "User reactivated"); load(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const changeRole = async (u, role) => {
    try { await api.patch(`/users/${u.id}`, { role }); toast.success("Role updated"); load(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const resetPin = async (u) => {
    const pin = window.prompt(`New PIN for ${u.name} (4–6 digits)`);
    if (!pin) return;
    try { await api.post(`/users/${u.id}/reset-pin`, { new_pin: pin }); toast.success("PIN reset — user sessions revoked"); } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div>
      <PageHeader title="Users" subtitle="One user belongs to exactly one tenant. The last active Admin cannot be demoted." testId="users-header"
        actions={<button onClick={() => { setF({ role: "garage_supervisor" }); setOpen(true); }} data-testid="add-user-btn" className="flex h-10 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-[#0071e3]"><Plus className="h-4 w-4" />Add user</button>} />
      {!rows ? <Spinner /> : (
        <div className="fv-card divide-y p-1" data-testid="users-list">
          {rows.map((u) => (
            <div key={u.id} className="fv-dense-row cursor-default" data-testid={`user-row-${u.phone}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">{u.name}{u.id === me.id && <span className="ml-1 text-xs font-normal text-muted-foreground">(you)</span>}</p>
                <p className="truncate text-[11px] text-muted-foreground">{u.phone}{u.email ? ` · ${u.email}` : ""}{!u.is_active ? " · Deactivated" : ""}</p>
              </div>
              <Select data-testid={`user-role-select-${u.phone}`} className="h-8 w-auto text-xs" value={u.role} onChange={(e) => changeRole(u, e.target.value)} disabled={u.id === me.id}>{ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</Select>
              <button onClick={() => resetPin(u)} data-testid={`reset-pin-${u.phone}`} className="rounded-lg p-2 text-primary hover:bg-primary/10" aria-label="Reset PIN"><KeyRound className="h-4 w-4" /></button>
              {u.id !== me.id && <button onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.phone}`} className="rounded-lg p-2 text-primary hover:bg-primary/10" aria-label={u.is_active ? "Deactivate" : "Reactivate"}>{u.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}</button>}
            </div>
          ))}
        </div>
      )}
      <FormDialog open={open} onClose={() => setOpen(false)} title="Add user" onSubmit={create} testId="user-form-dialog">
        <Field label="Name" required><Input data-testid="user-name-input" value={f.name || ""} onChange={set("name")} required /></Field>
        <Field label="Phone" required hint="With country code, e.g. +91…"><Input data-testid="user-phone-input" type="tel" value={f.phone || ""} onChange={set("phone")} required /></Field>
        <Field label="Initial PIN" required hint="4 to 6 digits"><Input data-testid="user-pin-input" inputMode="numeric" maxLength={6} value={f.pin || ""} onChange={(e) => setF({ ...f, pin: e.target.value.replace(/\D/g, "") })} required /></Field>
        <Field label="Role" required><Select data-testid="user-role-input" value={f.role} onChange={set("role")}>{ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</Select></Field>
        <Field label="Email"><Input data-testid="user-email-input" type="email" value={f.email || ""} onChange={set("email")} /></Field>
      </FormDialog>
    </div>
  );
}
